import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Container, 
  Typography, 
  Box, 
  Button, 
  CircularProgress, 
  Grid, 
  Accordion, 
  AccordionSummary, 
  AccordionDetails, 
  Alert 
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import axiosInstance from '../utils/axiosConfig';
import BandEventInfo from '../components/performance/BandEventInfo';
import PerformanceRevenue from '../components/performance/PerformanceRevenue';
import PerformanceCosts from '../components/performance/PerformanceCosts';
import TicketSalesSection from '../components/performance/TicketSalesSection';
import ActionSection from '../components/performance/ActionSection';
import NotesSection from '../components/performance/NotesSection';
import StaffRoster from '../components/performance/StaffRoster';
import TitoSlug from '../components/performance/TitoSlug';
import PerformanceFinancials from '../components/performance/PerformanceFinancials';
import FinancialOverview from '../components/performance/FinancialOverview';
import ScrollToTopButton from '../components/ScrollToTopButton';


// Import both types for compatibility
import { Performance as PerformanceDB } from '../types/mongodb';
import { 
  Performance as PerformanceCustom, 
  PerformanceCostsBreakdown,
  calculateTotalCost,
  calculateTotalIncome,
  calculateProfitLoss
} from '../types/Performance';

// Import adapter functions to convert between types
import { toDBModel, fromDBModel } from '../utils/performance-adapter';

import { BandRecommendation, Event, Venue, Staff } from '../types/mongodb';
import { Strand } from '../types/Strand';

const isFullStrand = (strand: string | Strand | undefined): strand is Strand => {
  return typeof strand === 'object' && strand !== null && 'startDate' in strand;
};

const PerformanceDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [performance, setPerformance] = useState<PerformanceCustom | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCost, setTotalCost] = useState<number>(0);
  const [totalIncome, setTotalIncome] = useState<number>(0);
  const [profitLoss, setProfitLoss] = useState<number>(0);
  const [ticketsSold, setTicketsSold] = useState<number>(0);
  const [ticketSales, setTicketSales] = useState<number>(0);
  const [costBreakdown, setCostBreakdown] = useState<PerformanceCostsBreakdown>({
    fee: 0,
    travel: 0,
    accommodation: 0,
    perDiem: 0,
    equipment: 0,
    other: 0,
    contingency: 0,
  });
  const [ticketConfig, setTicketConfig] = useState<{
    ticketTypes: Array<{
      name: string;
      quantity: number;
      price: number;
      vatRate: number;
      vatExempt: boolean;
      revenueImpact: boolean;
    }>;
  } | null>(null);
  const assignStaffRef = useRef<HTMLDivElement | null>(null);

  const handleScrollToStaff = () => {
    assignStaffRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const calculateTravelTotal = (travel: any) => {
    return (travel?.flights || 0) + 
           (travel?.groundTransport || 0) + 
           (travel?.other || 0);
  };
  
  const calculateAccommodationTotal = (accommodation: any) => {
    if (!accommodation) return 0;
    
    // Use the correctly structured accommodation object
    const singleRooms = accommodation.singleRooms || {};
    const twinRooms = accommodation.twinRooms || {};
    
    const singleRoomsCost = 
      (Number(singleRooms.quantity) || 0) * 
      (Number(singleRooms.costPerRoom) || 0) * 
      (Number(singleRooms.totalNights) || 1);
      
    const twinRoomsCost = 
      (Number(twinRooms.quantity) || 0) * 
      (Number(twinRooms.costPerRoom) || 0) * 
      (Number(twinRooms.totalNights) || 1);
      
    return singleRoomsCost + twinRoomsCost;
  };
  
  const calculatePerDiemTotal = (perDiem: any) => {
    if (!perDiem) return 0;
    return (Number(perDiem.dailyRate) || 0) * 
           (Number(perDiem.numberOfDays) || 1);
  };
  
  const calculateEquipmentTotal = (equipment: any) => {
    if (!equipment) return 0;
    return (Number(equipment.rental) || 0) + 
           (Number(equipment.shipping) || 0) +
           (Number(equipment.backline) || 0);
  };

  // Calculate ticket revenue from ticket configuration
  const calculateTicketRevenue = () => {
    if (!ticketConfig || !ticketConfig.ticketTypes) return 0;
    
    return ticketConfig.ticketTypes.reduce((sum, ticket) => {
      return sum + ((ticket.quantity || 0) * (ticket.price || 0));
    }, 0);
  };

  const fetchPerformance = useCallback(async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      const response = await axiosInstance.get<PerformanceDB>(
        `/api/performances/${id}?populate=staff,strand`
      );
      
      // Convert the DB model to our custom type
      const customPerformance = fromDBModel(response.data);
      setPerformance(customPerformance);
      updateFinancials(customPerformance);
      setError(null);
    } catch (error) {
      console.error('Error fetching performance:', error);
      setError('Failed to fetch performance details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchTicketConfig = useCallback(async () => {
    if (!id) return;
    
    try {
      const response = await axiosInstance.get(
        `/api/ticket-configs/search`, {
          params: {
            performanceId: id,
            selectionMethod: 'event'
          }
        }
      );
      setTicketConfig(response.data);
    } catch (error) {
      console.error('Error fetching ticket config:', error);
    }
  }, [id]);

  useEffect(() => {
    fetchPerformance();
    fetchTicketConfig();
  }, [fetchPerformance, fetchTicketConfig]);

  const updateFinancials = (data: PerformanceCustom) => {
    
    // Use the helper functions from our custom types
    const total = calculateTotalCost(data);
    
    // Calculate income from ticket config if available, otherwise use data.revenue
    const calculatedTicketIncome = calculateTicketRevenue();
    const income = calculatedTicketIncome > 0 ? calculatedTicketIncome : calculateTotalIncome(data);
    
    // Calculate profit/loss using our income and cost values
    const profit = income - total;
    
    setTotalCost(total);
    setTotalIncome(income);
    setProfitLoss(profit);
    
    // Extract bandFee for clarity
    const bandFee = Number(data.costs?.bandFee || 0);
    
    // Calculate technical costs
    const technicalCosts = 
      (Number(data.costs?.technical?.soundEngineer) || 0) + 
      (Number(data.costs?.technical?.lightingEngineer) || 0) + 
      (Number(data.costs?.technical?.backline) || 0) + 
      (Number(data.costs?.technical?.other) || 0);
    
    // Calculate all travel costs
    const travelCosts = calculateTravelTotal(data.costs?.travel);
    
    // Calculate accommodation costs
    const accommodationCosts = calculateAccommodationTotal(data.costs?.accommodation);
    
    // Calculate per diem costs
    const perDiemCosts = calculatePerDiemTotal(data.costs?.perDiem);
    
    // Calculate equipment costs
    const equipmentCosts = calculateEquipmentTotal(data.costs?.equipment);
    
    // Calculate additional costs
    const additionalCosts =
      (Number(data.costs?.marketing) || 0) + 
      (Number(data.costs?.insurance) || 0) + 
      (Number(data.costs?.visas) || 0) + 
      (Number(data.costs?.other) || 0);
    
    // Sum all costs EXCEPT bandFee
    const totalWithoutBandFee = 
      travelCosts + 
      accommodationCosts + 
      perDiemCosts + 
      equipmentCosts + 
      technicalCosts + 
      additionalCosts;
    
    // Calculate full cost (with bandFee)
    const totalBeforeContingency = bandFee + totalWithoutBandFee;
    
    // Calculate contingency
    const contingencyRate = Number(data.contingencyRate || 10);
    const contingencyAmount = (totalBeforeContingency * contingencyRate) / 100;
    
    // Update cost breakdown to explicitly include bandFee
    setCostBreakdown({
      fee: bandFee,
      travel: travelCosts,
      accommodation: accommodationCosts,
      perDiem: perDiemCosts,
      equipment: equipmentCosts,
      other: technicalCosts + additionalCosts,
      contingency: contingencyAmount
    });

    
    // Set ticket sales info
    setTicketsSold(Math.floor(data.revenue.expectedOccupancy || 0));
    setTicketSales((data.revenue.ticketPrice || 0) * (data.revenue.expectedOccupancy || 0));
  };

  const handleStaffAssignment = async (updatedPerformance: PerformanceDB) => {
    // Convert from DB model to custom type
    const customPerformance = fromDBModel(updatedPerformance);
    
    setPerformance(prev => {
      if (!prev) return customPerformance;
      return { ...prev, staff: customPerformance.staff };
    });
    
    if (performance) {
      updateFinancials(customPerformance);
    }
  };

  const handleUpdate = (updatedPerformance: PerformanceDB) => {
    // Convert from DB model to custom type
    const customPerformance = fromDBModel(updatedPerformance);
    setPerformance(customPerformance);
    updateFinancials(customPerformance);
  };

  const handleSave = async () => {
    if (!performance) return;

    try {
      // Convert back to DB model for API
      const dbPerformance = toDBModel(performance);
      
      const response = await axiosInstance.put<PerformanceDB>(
        `/api/performances/${performance._id}`,
        dbPerformance
      );
      
      // Convert response back to custom type
      const updatedPerformance = fromDBModel(response.data);
      setPerformance(updatedPerformance);
      updateFinancials(updatedPerformance);
      alert('Changes saved successfully.');
    } catch (error) {
      console.error('Error saving changes:', error);
      alert('Failed to save changes.');
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ textAlign: 'center', mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ textAlign: 'center', mt: 4 }}>
        <Typography color="error">{error}</Typography>
      </Container>
    );
  }

  if (!performance) {
    return null;
  }

  // Convert to DB model for component compatibility
  const performanceForComponents = toDBModel(performance);
  const hasStrand = performance.strandId !== undefined;

  // Calculate revenue from ticket configuration for PerformanceFinancials
  const ticketRevenue = calculateTicketRevenue();
  const ticketQuantity = ticketConfig ? 
    ticketConfig.ticketTypes.reduce((sum, ticket) => sum + (ticket.quantity || 0), 0) : 0;
  const avgTicketPrice = ticketQuantity > 0 ? ticketRevenue / ticketQuantity : 0;



  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Performance Details
        </Typography>
        <TitoSlug performance={performanceForComponents} />
      </Box>

      {!hasStrand && (
        <Alert severity="info" sx={{ mb: 3 }}>
          This performance is not currently assigned to any strand.
        </Alert>
      )}
           
      <Grid container spacing={4}>
        <Grid item xs={12} md={8}>
          <BandEventInfo 
            performance={{ 
              ...performanceForComponents, 
              band: performanceForComponents.band || '',
              event: performanceForComponents.event || ''
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <ActionSection 
            performance={performanceForComponents} 
            onManageStaffClick={handleScrollToStaff}
          />
        </Grid>
      </Grid>



{performance && ticketConfig && (
  <Grid item xs={12}>
    <PerformanceFinancials
      ticketConfig={ticketConfig} // Add this missing prop
      performance={{
        contingencyRate: performance.contingencyRate || 10,
        costs: {
          // Make sure bandFee is directly included
          bandFee: Number(performance.costs?.bandFee || 0),
          
          travel: {
            flights: 0,
            groundTransport: 0,
            other: 0,
            total: calculateTravelTotal(performance.costs?.travel || {})
          },
          
          accommodation: {
            total: calculateAccommodationTotal(performance.costs?.accommodation || {})
          },
          
          perDiem: {
            total: calculatePerDiemTotal(performance.costs?.perDiem || {})
          },
          
          equipment: {
            total: calculateEquipmentTotal(performance.costs?.equipment || {})
          },
          
          technical: {
            total: (
              Number(performance.costs?.technical?.soundEngineer || 0) + 
              Number(performance.costs?.technical?.lightingEngineer || 0) + 
              Number(performance.costs?.technical?.backline || 0) + 
              Number(performance.costs?.technical?.other || 0)
            )
          },
          
          additional: {
            marketing: 0,
            insurance: 0,
            visas: 0,
            total: (
              Number(performance.costs?.marketing || 0) + 
              Number(performance.costs?.insurance || 0) + 
              Number(performance.costs?.visas || 0)
            )
          },
          
          summary: {
            // Make sure the summary is properly calculated
            totalCosts: totalCost - costBreakdown.fee - costBreakdown.contingency,
            contingency: costBreakdown.contingency,
            grandTotal: totalCost
          }
        },
        // Add revenue information properly structured
        revenue: {
          ticketPrice: avgTicketPrice,
          expectedOccupancy: ticketQuantity,
          totalRevenue: ticketRevenue,
          sponsorship: 0,
          merchandiseSales: 0,
          otherRevenue: 0
        }
      }}
    />
  </Grid>
)}

      <Grid container spacing={4} sx={{ mt: 2 }}>
        <Grid item xs={12} md={12} ref={assignStaffRef}> 
          <StaffRoster 
            performanceId={performance._id}
            onAssignmentComplete={handleStaffAssignment}
          />
        </Grid>
        <Grid item xs={12} md={6} sx={{ width: '100%' }}>
          <NotesSection notes={performance.notes || ''} />
        </Grid>
      </Grid>

      <Accordion sx={{ mt: 4 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>Detailed Financial Management</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <PerformanceRevenue 
                performance={performanceForComponents}
                onUpdate={handleUpdate}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <PerformanceCosts
                performance={performanceForComponents}
                onUpdate={handleUpdate}
              />
            </Grid>
            <Grid item xs={12}>
              <TicketSalesSection 
                ticketsSold={ticketsSold}
                ticketSales={ticketSales}
                totalIncome={totalIncome}
                totalCost={totalCost}
                costBreakdown={costBreakdown}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      <Box sx={{ textAlign: 'right', mt: 4 }}>
        <Button
          variant="outlined"
          sx={{ mr: 2 }}
          onClick={() => navigate(`/admin/performances/edit/${performance._id}`)}
        >
          Edit Performance Details
        </Button>
        <Button variant="contained" onClick={handleSave} sx={{ mr: 2 }}>
          Save Changes
        </Button>
        <Button variant="contained" onClick={() => navigate('/admin/performances')}>
          Back to Performances
        </Button>
      </Box>
            <ScrollToTopButton 
        backgroundColor="#1976d2"
        color="#ffffff"
        scrollThreshold={300}
        size={50}
      />
    </Container>
  );
};

export default PerformanceDetailPage;